/**
 * FreeShippingBar — Progress bar toward free shipping threshold.
 */

import { siteConfig } from "@/config/site";
import { formatPrice } from "@/components/forms/price-display";
import { cn } from "@/lib/utils";
import { useStoreShippingSettings } from "@/lib/store-settings";

type FreeShippingBarProps = {
  subtotal: number;
  className?: string;
};

export function FreeShippingBar({ subtotal, className }: FreeShippingBarProps) {
  const { freeShippingThreshold } = useStoreShippingSettings();
  const threshold = freeShippingThreshold || siteConfig.shipping.freeThreshold;
  const progress = Math.min(100, (subtotal / threshold) * 100);
  const remaining = threshold - subtotal;
  const qualified = subtotal >= threshold;

  return (
    <div className={cn("border bg-secondary/20 p-4", className)}>
      <p className="text-xs text-foreground">
        {qualified
          ? "🎉 You've unlocked complimentary shipping!"
          : `${formatPrice(remaining)} away from free shipping.`}
      </p>
      <div
        className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-border"
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={
          qualified ? "Free shipping unlocked" : `${formatPrice(remaining)} away from free shipping`
        }
      >
        <div
          className="h-full bg-primary transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}
